import React, { useCallback, useMemo } from 'react';
import { View } from 'react-native';
import { launchImageLibrary } from 'utils/packages/imagePicker';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import { OnboardingFlow, POAType } from 'utils/types/navigation';
import { AppView, Button, ButtonGroup, CardView, SCROLL_INDICATOR_INSETS, Spacer, rem, withAnimatedNavigationHeader, } from 'design-system';
import Animated from 'react-native-reanimated';
import { useMarginBottom } from 'hooks/useMarginBottom';
import { HEIGHT } from 'design-system/Button';
import ScreenInfoHeader from 'design-system/ScreenInfoHeader';
import { assertUnreachable } from 'utils';
import Text from 'design-system/Text';
import IconInfoTriangle from 'design-system/icons/IconInfoTriangle';
import createStyleSheets from 'utils/createStyleSheets';
import useStyles from 'hooks/useStyles';
import Links from 'utils/links';
import CardWithChecklist from 'design-system/CardWithChecklist';
import useColors from 'hooks/useColors';
import { isWeb } from '../../../constants';
const btnOffset = 2 * HEIGHT + rem(24);
const ProofOfAddressScreen = withAnimatedNavigationHeader(({ route, navigation, scrollHandler }) => {
    const { params } = route;
    const colors = useColors();
    const styles = useStyles(styleSet);
    const onOpenCamera = useCallback(() => {
        navigation.navigateDeprecated('InvestScanProofOfAddress', { flow: params?.flow, address: params?.address });
        // navigation in current version of react-navigation is not memoized
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const onOpenLibrary = useCallback(async () => {
        try {
            const { assets } = await new Promise((resolve, reject) => {
                launchImageLibrary({ mediaType: 'photo' }, (response) => {
                    if (response.assets && response.assets.length) {
                        resolve(response);
                    }
                    else {
                        reject(new Error());
                    }
                });
            });
            if (assets && assets.length) {
                const { uri, width, height, type } = assets[0];
                if (uri && width && height) {
                    if (params?.flow === OnboardingFlow.Address && !params?.address) {
                        return;
                    }
                    navigation.navigateDeprecated('InvestProofOfAddressPreview', {
                        uri,
                        type,
                        width,
                        height,
                        flow: params?.flow,
                        address: params?.address,
                    });
                }
            }
        }
        catch {
            // user cancelled, nothing to do
        }
        // navigation in current version of react-navigation is not memoized
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const infoObj = useMemo(() => {
        switch (params.poaType) {
            case POAType.BILL: {
                return { title: 'Upload utility bill or statement', docTitle: 'a “utility bill” or an “official statement”' };
            }
            case POAType.ACCOUNT_STATEMENT: {
                return { title: 'Upload personal bank account statement', docTitle: 'an “official bank statement”' };
            }
            case POAType.DRIVING_LICENCE: {
                return { title: 'Upload UK photocard driving license', docTitle: 'a “driver license”' };
            }
            case POAType.HMRC_INSURANCE: {
                return { title: 'HMRC correspondence', docTitle: 'an “HMRC correspondence”' };
            }
            case POAType.DWP_INSURANCE: {
                return { title: 'DWP correspondence', docTitle: 'an “DWP correspondence”' };
            }
            default:
                assertUnreachable(params.poaType);
                return { title: '', docTitle: '' };
        }
    }, [params.poaType]);
    const goBack = useCallback(() => {
        navigation.goBack();
    }, []);
    const openRequirements = useCallback(() => {
        Links.openKycProcessLink();
    }, []);
    const { paddingHorizontalStyle } = useAppFrameDimensions(isWeb);
    const contentContainerStyles = useMarginBottom('paddingBottom', btnOffset, paddingHorizontalStyle);
    const Footer = useMemo(() => (<>
          <Spacer h={8}/>
          <CardView withPadding noMargin style={styles.infoCard}>
            <View style={styles.row}>
              <IconInfoTriangle width={24} height={24} color={colors.text.textOnBrand}/>
              <Spacer w={8}/>
              <View style={styles.flex}>
                {params.poaType === POAType.DRIVING_LICENCE ? (<Text OnBrand TextThin-14>
                    The document must{' '}
                    <Text OnBrand Text-14>
                      valid
                    </Text>{' '}
                    and{' '}
                    <Text OnBrand Text-14>
                      not expired
                    </Text>
                    .
                  </Text>) : (<Text OnBrand TextThin-14>
                    The document must be issued within{' '}
                    <Text OnBrand Text-14>
                      3 months
                    </Text>
                    .
                  </Text>)}
              </View>
            </View>
          </CardView>
        </>), [colors.text.textOnBrand, params.poaType, styles.flex, styles.infoCard, styles.row]);
    const checkList = useMemo(() => {
        switch (params.poaType) {
            case POAType.ACCOUNT_STATEMENT:
                return [
                    'Your address is visible',
                    'Bank details are visible',
                    <Text TextThin-14 Secondary key="ACCOUNT_STATEMENT.name is visible">
              Your{' '}
              <Text Text-14 Secondary>
                full legal
              </Text>{' '}
              name is visible
            </Text>,
                    'Full document is in the frame',
                ];
            case POAType.DRIVING_LICENCE:
                return [
                    'Your address is visible',
                    <Text TextThin-14 Secondary key="DRIVING_LICENCE.name is visible">
              Your{' '}
              <Text Text-14 Secondary>
                full legal
              </Text>{' '}
              name is visible
            </Text>,
                    <Text TextThin-14 Secondary key="DRIVING_LICENCE.not a photocopy of the original">
              The document is{' '}
              <Text Text-14 Secondary>
                not a photocopy of the original
              </Text>
            </Text>,
                    'Full document is in the frame',
                ];
            default:
                return [
                    'Your address is visible',
                    <Text TextThin-14 Secondary key="default.name is visible">
              Your{' '}
              <Text Text-14 Secondary>
                full legal
              </Text>{' '}
              name is visible
            </Text>,
                    'Full document is in the frame',
                ];
        }
    }, [params.poaType]);
    return (<AppView withFrame={false}>
        <Animated.ScrollView onScroll={scrollHandler} scrollEventThrottle={16} contentContainerStyle={contentContainerStyles} scrollIndicatorInsets={SCROLL_INDICATOR_INSETS}>
          <ScreenInfoHeader smallSubTitle title={infoObj.title} preTitle="Proof of address" subTitle="Capture the entire document and make sure that all the details are clearly visible."/>
          <CardWithChecklist title="Make sure that:" checkList={checkList} footer={Footer}/>
          <View style={styles.disclaimer}>
            <Text TextThin-14 Secondary>
              Anything other than {infoObj.docTitle} will{' '}
              <Text Text-14 Secondary>
                be rejected
              </Text>
              . Make sure that the document is compliant with our{' '}
              <Text Primary TextThin-14 suppressHighlighting onPress={openRequirements}>
                requirements
              </Text>{' '}
              or{' '}
              <Text Primary TextThin-14 onPress={goBack} suppressHighlighting>
                choose another option
              </Text>
              .
            </Text>
          </View>
        </Animated.ScrollView>
        <ButtonGroup type="floating">
          <Button brandReversed title="Choose from library" onPress={onOpenLibrary}/>
          <Button brand title="Take a photo" onPress={onOpenCamera}/>
        </ButtonGroup>
      </AppView>);
}, ({ colors }) => ({
    headerColorOutputRange: [colors.background.dark, colors.cards.onDark],
}));
const styleSet = createStyleSheets((colors) => ({
    disclaimer: {
        paddingHorizontal: rem(16),
    },
    row: {
        flex: 1,
        alignItems: 'center',
        flexDirection: 'row',
    },
    infoRow: {
        flex: 1,
        alignItems: 'center',
        flexDirection: 'row',
        paddingVertical: rem(8),
    },
    infoCard: {
        backgroundColor: colors.buttons.brandGroup,
    },
    flex: {
        flex: 1,
    },
    checkContainer: {
        backgroundColor: colors.buttons.brandGroup,
    },
}));
export default ProofOfAddressScreen;
